import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'
import { resolveRangePreset } from '@w7/ui'
import type { DateRange } from '@/features/reports/types'
import { isoDay, ll } from '@/lib/format'

export type PresetId = 'today' | '7d' | '30d' | '90d'

export const RANGE_PRESETS: { id: PresetId; label: string; short: string; days: number }[] = [
  { id: 'today', label: 'Today', short: '1D', days: 1 },
  { id: '7d', label: 'Last 7 days', short: '7D', days: 7 },
  { id: '30d', label: 'Last 30 days', short: '30D', days: 30 },
  { id: '90d', label: 'Last 90 days', short: '90D', days: 90 },
]

export interface RangeValue {
  range: DateRange & { label: string; days: number }
  preset: PresetId
  setPreset: (id: PresetId) => void
}

const RangeContext = createContext<RangeValue | null>(null)

export function DateRangeProvider({ children }: { children: ReactNode }) {
  const [preset, setPreset] = useState<PresetId>('30d')

  const value = useMemo<RangeValue>(() => {
    const days = RANGE_PRESETS.find((p) => p.id === preset)?.days ?? 30
    const now = new Date()
    const to = isoDay(now)
    const from = isoDay(now.getTime() - (days - 1) * 86_400_000)
    const label = days === 1 ? ll(now) : `${ll(from)} – ${ll(to)}`
    return { range: { from, to, label, days }, preset, setPreset }
  }, [preset])

  return <RangeContext.Provider value={value}>{children}</RangeContext.Provider>
}

export function useDateRange(): RangeValue {
  const value = useContext(RangeContext)
  if (!value) {
    const resolved = resolveRangePreset('last-30') ?? {
      from: isoDay(new Date()),
      to: isoDay(new Date()),
    }
    const label = `${ll(resolved.from)} – ${ll(resolved.to)}`
    return {
      range: { ...resolved, label, days: 30 },
      preset: '30d',
      setPreset: () => {},
    }
  }
  return value
}
